import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverHeader,
  VStack,
  HStack,
  Text,
  Box,
  Button,
  useColorModeValue,
  Divider,
  Spinner,
  Center
} from '@chakra-ui/react';
import { FiBell } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { NotificationService, Notification } from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';

const NotificationBell: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribeNotifications = NotificationService.subscribeToNotifications(
      user.uid,
      (notifs) => {
        setNotifications(notifs);
        setLoading(false);
      }
    );

    const unsubscribeCount = NotificationService.subscribeToUnreadCount(
      user.uid,
      (count) => {
        setUnreadCount(count);
      }
    );

    return () => {
      unsubscribeNotifications();
      unsubscribeCount();
    };
  }, [user]);

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await NotificationService.markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    if (user) {
      await NotificationService.markAllAsRead(user.uid);
    }
  };

  const formatDate = (date: Date | Timestamp) => {
    if (!date) return 'Just now';
    const dateObj = date instanceof Date ? date : date.toDate();
    const now = new Date();
    const diff = now.getTime() - dateObj.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return dateObj.toLocaleDateString();
  };

  if (!user) {
    return null;
  }

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-end">
      <PopoverTrigger>
        <Box position="relative">
          <IconButton
            aria-label="Notifications"
            icon={<IconWrapper icon={FiBell} size={20} />}
            variant="ghost"
            size="md"
            onClick={() => setIsOpen(!isOpen)}
          />
          {unreadCount > 0 && (
            <Badge
              position="absolute"
              top="-2px"
              right="-2px"
              colorScheme="red"
              borderRadius="full"
              fontSize="xs"
              minW="18px"
              h="18px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent w="400px" maxH="500px" bg={bgColor} borderColor={borderColor}>
        <PopoverHeader
          borderBottom="1px solid"
          borderColor={borderColor}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={3}
        >
          <Text fontWeight="bold" color={textColor}>
            Notifications
          </Text>
          {unreadCount > 0 && (
            <Button size="xs" variant="ghost" onClick={handleMarkAllAsRead}>
              Mark all as read
            </Button>
          )}
        </PopoverHeader>
        <PopoverBody p={0} maxH="450px" overflowY="auto">
          {loading ? (
            <Center py={8}>
              <Spinner size="md" color="purple.500" />
            </Center>
          ) : notifications.length === 0 ? (
            <Center py={8}>
              <VStack gap={2}>
                <IconWrapper icon={FiBell} size={32} color={labelColor} />
                <Text color={labelColor} fontSize="sm">
                  No notifications yet
                </Text>
              </VStack>
            </Center>
          ) : (
            <VStack align="stretch" spacing={0}>
              {notifications.map((notification, index) => (
                <Box key={notification.id}>
                  <Box
                    p={3}
                    bg={notification.read ? 'transparent' : hoverBg}
                    cursor="pointer"
                    onClick={() => handleNotificationClick(notification)}
                    _hover={{ bg: hoverBg }}
                    borderLeft={notification.read ? 'none' : '3px solid'}
                    borderLeftColor="purple.500"
                  >
                    <VStack align="start" gap={1}>
                      <HStack w="100%" justify="space-between">
                        <Text fontSize="sm" fontWeight={notification.read ? 'normal' : 'semibold'} color={textColor}>
                          {notification.title}
                        </Text>
                        <Text fontSize="xs" color={labelColor}>
                          {formatDate(notification.createdAt as any)}
                        </Text>
                      </HStack>
                      <Text fontSize="xs" color={labelColor} noOfLines={2}>
                        {notification.message}
                      </Text>
                    </VStack>
                  </Box>
                  {index < notifications.length - 1 && <Divider />}
                </Box>
              ))}
            </VStack>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;

